"use client";

import { TranslatedLink } from "components/atoms/TranslatedLink/TranslatedLink";
import { CBButton } from "components/InlineBlocks/CBButton";
import { HighlightedText } from "components/InlineBlocks/HighlightedText";
import { ImageBlock } from "components/InlineBlocks/ImageBlock";
import { Table } from "components/InlineBlocks/Table";
import dynamic from "next/dynamic";

const Video = dynamic(
  () => import("components/InlineBlocks/Video").then((module) => ({ default: module.Video })),
  { ssr: false },
);
import React, { FC, memo, useMemo } from "react";
import {
  renderNodeRule,
  StructuredText as DatoStructuredText,
  StructuredTextGraphQlResponse,
} from "react-datocms";
import { isParagraph } from "datocms-structured-text-utils";
import { isHeading } from "datocms-structured-text-utils";
import s from "./RichtextSection.module.scss";
import { render as toPlainText } from "datocms-structured-text-to-plain-text";
import { sanitizeNbspDeep } from "utils/text";

interface Props {
  structuredText: StructuredTextGraphQlResponse<any, any>;
}

export const StructuredText: FC<Props> = memo(({ structuredText }) => {
  const data = useMemo(() => sanitizeNbspDeep(structuredText), [structuredText]);

  return (
    <DatoStructuredText
      data={data}
      renderInlineRecord={({ record }) => {
        if (record.__typename === "ReusableTextRecord") {
          return (
            <span className={s.ReusableText} dangerouslySetInnerHTML={{ __html: record.content }} />
          );
        }
        return null;
      }}
      renderLinkToRecord={({ record, children }) => {
        return <TranslatedLink record={record}>{children}</TranslatedLink>;
      }}
      renderBlock={({ record }) => {
        switch (record.__typename) {
          case "DownloadButtonRecord":
          case "HubspotButtonRecord":
          case "ButtonRecord":
          case "ButtonExternalRecord":
            return <CBButton {...record} />;
          case "ImageRecord":
            return <ImageBlock data={record} />;
          case "VideoRecord":
            return <Video data={record} />;
          case "HighlightedTextRecord":
            return <HighlightedText {...record} />;
          case "TableRecord":
            return <Table {...record} />;
          default:
            return null;
        }
      }}
      customNodeRules={[
        renderNodeRule(isHeading, ({ node, children, key }) => {
          const HeadingTag = `h${node.level}` as any;
          const anchor = toPlainText(node)
            ?.toLowerCase()
            .replace(/ /g, "-")
            .replace(/[^\w-]+/g, "");

          return (
            <HeadingTag id={anchor} key={key}>
              {children}
            </HeadingTag>
          );
        }),
        renderNodeRule(isParagraph, ({ children, key, node }) => {
          const isEmptyNode = node.children.every(
            (child) => child.type === "span" && child.value === "",
          );
          if (isEmptyNode) return <br key={key} />;
          return <p key={key}>{children}</p>;
        }),
      ]}
    />
  );
});

StructuredText.displayName = "StructuredText";
